/**
 * Shopify Cart Automation Script
 * 3-Step Process:
 * 1. Create cart groups based on conditional logic (cart + customer data)
 * 2. Calculate derived variables for each group
 * 3. Evaluate rules and execute actions if all rules evaluate to true
 */

/**
 * Currency Utilities Class
 * Handles currency conversion using Shopify's CDN currencies.js and Shopify.currency
 * Supports only Shopify's official currency conversion methods
 */
class CurrencyUtils {
  constructor() {
    this.shopBaseCurrency = null;
    this.currentPresentmentCurrency = null;
    this.exchangeRate = 1.0;
    this.isCurrenciesJsLoaded = false;
    this.init();
  }



  init() {
    // Detect Shopify's currency setup
    this.detectCurrencySetup();
    // console.log('Dollarlabs: Currency setup detected:', {
    //   baseCurrency: this.shopBaseCurrency,
    //   presentmentCurrency: this.currentPresentmentCurrency,
    //   exchangeRate: this.exchangeRate,
    //   currenciesJsLoaded: this.isCurrenciesJsLoaded
    // });
  }

  detectCurrencySetup() {
    try {
      // Method 1: Check if Shopify CDN currencies.js is loaded
      if (window.Currency && typeof window.Currency.convert === 'function') {
        this.isCurrenciesJsLoaded = true;
      }

      // Method 2: Get exchange rate from Shopify.currency if available
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
        this.exchangeRate = parseFloat(window.Shopify.currency.rate) || 1.0;
      }

      // Method 3: Try to get currencies from Shopify objects, but validate them
      let shopifyCurrencyActive = window.Shopify?.currency?.active;
      let shopifyShopCurrency = window.Shopify?.shop?.currency;

      // Validate and use only real currency codes (reject CurrencyDrop and similar)
      const isValidCurrency = (currency) => {
        return currency && 
               currency !== 'CurrencyDrop' && 
               currency.length === 3 && 
               /^[A-Z]{3}$/.test(currency);
      };

      // Set presentment currency from Shopify if valid
      if (isValidCurrency(shopifyCurrencyActive)) {
        this.currentPresentmentCurrency = shopifyCurrencyActive;
      }

      // Set base currency from Shopify if valid
      if (isValidCurrency(shopifyShopCurrency)) {
        this.shopBaseCurrency = shopifyShopCurrency;
      }

      // Method 4: Try meta tag as fallback
      const metaCurrency = document.querySelector('meta[name="currency"]');
      if (metaCurrency && isValidCurrency(metaCurrency.content)) {
        if (!this.currentPresentmentCurrency) {
          this.currentPresentmentCurrency = metaCurrency.content;
        }
        if (!this.shopBaseCurrency) {
          this.shopBaseCurrency = metaCurrency.content;
        }
      }

      // Method 5: Use cart object when available (will be set later in getCurrencyForCalculation)
      // This is the most reliable source for presentment currency

      // Method 6: Final fallbacks based on your store setup
      if (!this.shopBaseCurrency) {
        // Based on your Shopify country being "IN", assume INR base currency
        this.shopBaseCurrency = 'INR';
      }

      if (!this.currentPresentmentCurrency) {
        this.currentPresentmentCurrency = this.shopBaseCurrency;
      }

      // Method 7: Log detected values for debugging
      // console.log('Dollarlabs: Currency detection details:', {
      //   shopifyShopCurrency: shopifyShopCurrency,
      //   shopifyCurrencyActive: shopifyCurrencyActive,
      //   shopifyCurrencyRate: window.Shopify?.currency?.rate,
      //   shopifyCountry: window.Shopify?.country,
      //   metaCurrency: metaCurrency?.content,
      //   detectedBaseCurrency: this.shopBaseCurrency,
      //   detectedPresentmentCurrency: this.currentPresentmentCurrency,
      //   exchangeRate: this.exchangeRate
      // });

    } catch (error) {
      // console.warn('Dollarlabs: Could not detect currency setup:', error);
      this.shopBaseCurrency = 'INR'; // Your known base currency based on country
      this.currentPresentmentCurrency = 'INR'; // Default to same as base
      this.exchangeRate = 1.0;
    }
  }

  /**
   * Determines if we should use base currency or presentment currency for calculations
   * @param {Object} config - The configuration object that may specify currency preference
   * @param {Object} cart - The cart object to get currency from
   * @returns {Object} Currency info with values to use for calculations
   */
  getCurrencyForCalculation(config, cart) {
    const configCurrencyMode = config?.currencyMode || 'auto'; // 'base', 'presentment', 'auto', or 'specific'
    const configSpecificCurrency = config?.specificCurrency;
    
    // Get cart currency from cart object - this is most reliable
    const cartCurrency = cart?.currency || this.currentPresentmentCurrency || 'INR';
    
    // Override presentment currency if we got it from cart and it's valid
    const isValidCurrency = (currency) => {
      return currency && 
             currency !== 'CurrencyDrop' && 
             currency.length === 3 && 
             /^[A-Z]{3}$/.test(currency);
    };
    
    if (cart?.currency && isValidCurrency(cart.currency) && cart.currency !== this.currentPresentmentCurrency) {
      this.currentPresentmentCurrency = cart.currency;
    }
    
    // Also use cart currency to infer base currency if we don't have a valid one
    if (!isValidCurrency(this.shopBaseCurrency)) {
      // If cart is in INR and we know your store is India-based, INR is likely the base
      if (cartCurrency === 'INR') {
        this.shopBaseCurrency = 'INR';
      } else {
        // Otherwise use cart currency as base (single currency store)
        this.shopBaseCurrency = cartCurrency;
      }
    }
    
    const isBaseCurrency = cartCurrency === this.shopBaseCurrency;

    let useCurrency, useBasedValues, conversionRate;

    if (configCurrencyMode === 'presentment') {
      // Use presentment currency and values
      useCurrency = cartCurrency;
      useBasedValues = 'presentment';
      conversionRate = 1.0; // No conversion needed, using presentment values directly
    } else if (configCurrencyMode === 'base') {
      // Force base currency mode
      useCurrency = this.shopBaseCurrency;
      useBasedValues = 'base';
      conversionRate = isBaseCurrency ? 1.0 : (1.0 / this.exchangeRate);
    } else if (configCurrencyMode === 'specific' && configSpecificCurrency) {
      // Use specific currency requested in config
      useCurrency = configSpecificCurrency;
      if (configSpecificCurrency === cartCurrency) {
        useBasedValues = 'presentment';
        conversionRate = 1.0;
      } else if (configSpecificCurrency === this.shopBaseCurrency) {
        useBasedValues = 'base';
        conversionRate = 1.0;
      } else {
        // Need to convert between currencies
        useBasedValues = 'base';
        conversionRate = this.getConversionRate(this.shopBaseCurrency, configSpecificCurrency);
      }
    } else {
      // Auto mode: use presentment currency when available, fall back to base currency
      if (!isBaseCurrency && this.exchangeRate && this.exchangeRate !== 1.0) {
        // Use presentment currency values when viewing in non-base currency
        useCurrency = cartCurrency;
        useBasedValues = 'presentment';
        conversionRate = 1.0;
      } else {
        // Use base currency
        useCurrency = this.shopBaseCurrency;
        useBasedValues = 'base';
        conversionRate = 1.0;
      }
    }

    return {
      currency: useCurrency,
      useBasedValues, // 'base' or 'presentment'
      conversionRate,
      isBaseCurrency,
      cartCurrency,
      exchangeRate: this.exchangeRate
    };
  }

  /**
   * Gets the appropriate price value from cart item based on currency settings
   */
  getItemPrice(item, currencyInfo, priceType = 'price') {
    let baseValue;
    
    if (currencyInfo.useBasedValues === 'presentment') {
      // Use presentment values (these are already in the display currency)
      switch (priceType) {
        case 'price':
          baseValue = item.presentment_price * 100; // Convert to cents
          break;
        case 'line_price':
          // Calculate: presentment_price * quantity, convert to cents
          baseValue = item.presentment_price * item.quantity * 100;
          break;
        case 'original_price':
          baseValue = (item.presentment_original_price || item.presentment_price) * 100;
          break;
        case 'original_line_price':
          baseValue = (item.presentment_original_price || item.presentment_price) * item.quantity * 100;
          break;
        case 'final_price':
          baseValue = (item.presentment_final_price || item.presentment_price) * 100;
          break;
        case 'final_line_price':
          baseValue = (item.presentment_final_price || item.presentment_price) * item.quantity * 100;
          break;
        default:
          baseValue = item.presentment_price * 100;
      }
    } else {
      // Use base currency values (already in cents)
      switch (priceType) {
        case 'price':
          baseValue = item.price;
          break;
        case 'line_price':
          baseValue = item.line_price;
          break;
        case 'original_price':
          baseValue = item.original_price;
          break;
        case 'original_line_price':
          baseValue = item.original_line_price;
          break;
        case 'final_price':
          baseValue = item.final_price;
          break;
        case 'final_line_price':
          baseValue = item.final_line_price;
          break;
        default:
          baseValue = item.price;
      }
    }

    // Apply conversion if needed
    return Math.round(baseValue * currencyInfo.conversionRate);
  }

  /**
   * Gets conversion rate between two currencies using Shopify's CDN currencies.js
   */
  getConversionRate(fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return 1.0;

    try {
      // Use Shopify CDN currencies.js if available
      if (this.isCurrenciesJsLoaded && window.Currency && typeof window.Currency.convert === 'function') {
        // Convert 100 units to get the rate
        const converted = window.Currency.convert(100, fromCurrency, toCurrency);
        return converted / 100;
      }

      // Fallback: use Shopify.currency.rate if available
      if (fromCurrency === this.shopBaseCurrency && toCurrency === this.currentPresentmentCurrency) {
        return this.exchangeRate;
      } else if (fromCurrency === this.currentPresentmentCurrency && toCurrency === this.shopBaseCurrency) {
        return 1.0 / this.exchangeRate;
      }

      // Last resort: return 1.0 (no conversion)
      // console.warn(`Dollarlabs: Could not determine conversion rate from ${fromCurrency} to ${toCurrency}. Make sure Shopify CDN currencies.js is loaded.`);
      return 1.0;
    } catch (error) {
      // console.warn('Dollarlabs: Error getting conversion rate:', error);
      return 1.0;
    }
  }

  /**
   * Converts an amount from one currency to another using Shopify's CDN currencies.js
   */
  convertAmount(amount, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return amount;

    try {
      // Use Shopify CDN currencies.js if available
      if (this.isCurrenciesJsLoaded && window.Currency && typeof window.Currency.convert === 'function') {
        return Math.round(window.Currency.convert(amount, fromCurrency, toCurrency));
      }

      // Fallback: use rate calculation
      const rate = this.getConversionRate(fromCurrency, toCurrency);
      return Math.round(amount * rate);
    } catch (error) {
      // console.warn('Dollarlabs: Error converting amount:', error);
      return amount;
    }
  }

  /**
   * Formats currency amount for display
   */
  formatMoney(amount, currency) {
    try {
      // Try using Shopify's Money formatting if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(amount, window.shop?.money_format || '${{amount}}');
      }

      // Basic fallback formatting
      const formatted = (amount / 100).toFixed(2);
      const currencySymbols = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'INR': '₹',
        'CAD': 'C$',
        'AUD': 'A$'
      };
      
      const symbol = currencySymbols[currency] || currency;
      return `${symbol}${formatted}`;
    } catch (error) {
      return `${currency} ${(amount / 100).toFixed(2)}`;
    }
  }
}

class CartAutomation {
  constructor(config) {
    this.config = config;
    this.cartWatcher = null;
    this.isProcessing = false;
    this.isAutomationTriggered = false;
    this.lastActionTimestamp = 0;
    this.currencyUtils = new CurrencyUtils();
    // Track variant IDs that are added as gifts/actions to prevent loops
    this.actionVariantIds = new Set();
    this.init();
  }

  init() {
    // console.log('Dollarlabs: Initializing CartAutomation');
    // Initialize cart watcher
    this.cartWatcher = new CartWatcher();
    this.cartWatcher.init();
    
    // Listen for cart changes
    window.addEventListener("cart_changed", (e) => {
      this.handleCartChange(e.detail);
    });
    
    // Initial evaluation
    this.evaluateCart();
    // console.log('Dollarlabs: CartAutomation initialization complete');
  }

  async handleCartChange(changes) {
    if (this.isProcessing) {
      // console.log('Dollarlabs: Cart change ignored - already processing');
      return;
    }
    
    // Check if this change was caused by our automation to prevent loops
    if (this.isAutomationTriggered) {
      // console.log('Dollarlabs: Cart change ignored - caused by automation');
      this.isAutomationTriggered = false; // Reset the flag
      return;
    }
    
    // console.log('Dollarlabs: Cart changed:', changes);
    await this.evaluateCart();
  }

  async evaluateCart() {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    
    try {
      const cart = await this.fetchCart();
      
      // Track all action variant IDs from all configs upfront to prevent loops
      // This ensures that gift/action products are excluded from group matching across all configs
      this.actionVariantIds.clear();
      this.config.forEach(configItem => {
        if (configItem.actions && Array.isArray(configItem.actions)) {
          configItem.actions.forEach(action => {
            if (action.variantId) {
              this.actionVariantIds.add(action.variantId);
            } else if (action.productId) {
              // If only productId is provided, track it as well
              this.actionVariantIds.add(action.productId);
            }
          });
        }
      });
      
      // console.log('Dollarlabs: Action variant IDs tracked for quantity counting:', Array.from(this.actionVariantIds));
      
      // Process each config (can have multiple configs)
      // console.log('Dollarlabs: Processing', this.config.length, 'configuration(s)');
      for (let i = 0; i < this.config.length; i++) {
        const configItem = this.config[i];
        // console.log('Dollarlabs: Checking config', (i + 1) + '/' + this.config.length + ':', configItem.name || 'Unnamed Config');
        await this.processConfig(configItem, cart);
      }
    } catch (error) {
      // console.error('Dollarlabs: Error evaluating cart:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async processConfig(configItem, cart) {
    // Action variant IDs are already tracked in evaluateCart() before processing configs
    // This ensures all action items are excluded from group matching
    
    // Get currency information for this configuration
    const currencyInfo = this.currencyUtils.getCurrencyForCalculation(configItem, cart);
    
    // Log currency information for debugging
    // console.log('Dollarlabs: Currency calculation info:', {
    //   configName: configItem.name,
    //   cartCurrency: cart.currency,
    //   shopBaseCurrency: this.currencyUtils.shopBaseCurrency,
    //   presentmentCurrency: this.currencyUtils.currentPresentmentCurrency,
    //   exchangeRate: this.currencyUtils.exchangeRate,
    //   currencyMode: configItem.currencyMode || 'auto',
    //   finalCurrencyInfo: currencyInfo
    // });
    
    // Create diagnostic object to track entire rule engine process
    const diagnostic = {
      timestamp: new Date().toISOString(),
      configName: configItem.name || 'Unnamed Config',
      currencyInfo: currencyInfo,
      cartSummary: {
        totalItems: cart.items.length,
        totalQuantity: cart.items.reduce((sum, item) => sum + item.quantity, 0),
        totalValue: cart.items.reduce((sum, item) => sum + this.currencyUtils.getItemPrice(item, currencyInfo, 'line_price'), 0),
        items: cart.items.map(item => ({
          variantId: item.variant_id,
          productTitle: item.product_title || item.title,
          quantity: item.quantity,
          price: this.currencyUtils.getItemPrice(item, currencyInfo, 'line_price')
        }))
      },
      groupCreation: {},
      groupVariables: {},
      ruleEvaluations: [],
      finalResult: false,
      actionsExecuted: []
    };

    // Store current cart and currency info for property access
    this.currentCart = cart;
    this.currentCurrencyInfo = currencyInfo;
    
    // Step 1: Create ALL cart groups (always includes "All" + custom groups)
    // Use fresh cart to ensure we have latest item properties (like _AutoAddedByScript)
    const freshCartForGroups = await this.fetchCart();
    const cartGroups = this.createCartGroups(freshCartForGroups, configItem.groupCriteria, diagnostic);
    
    // Step 2: Calculate derived variables for each group
    const groupVariables = {};
    for (const [groupName, group] of Object.entries(cartGroups)) {
      groupVariables[groupName] = this.calculateDerivedVariables(group.items, currencyInfo);
      diagnostic.groupVariables[groupName] = {
        itemCount: group.items.length,
        variables: groupVariables[groupName]
      };
    }
    
    // Step 3: Evaluate rules and execute actions
    const rulesResult = this.evaluateRules(groupVariables, configItem.rules, configItem.rulesLogic, diagnostic);
    diagnostic.finalResult = rulesResult;
    
    if (rulesResult) {
      diagnostic.actionsExecuted = await this.executeActions(configItem.actions, cart, diagnostic);
    } else {
      // Even if rules fail, check for auto_remove products that should be removed
      diagnostic.actionsExecuted = await this.checkAutoRemoveProducts(configItem.actions, cart, diagnostic);
    }

    // Output single comprehensive diagnostic log
    console.log('Dollarlabs: Rule Engine Diagnostic:', diagnostic);
  }

  // Step 1: Create ALL cart groups (always includes "All" + custom groups)
  createCartGroups(cart, groupCriteriaList, diagnostic = null) {
    const cartGroups = {};
    
    // Filter out ONLY items added by the script (items with _AutoAddedByScript property)
    // Do NOT exclude by variant ID - this allows same product/variant to be both eligible and gift
    const scriptAddedPropertyKey = '_AutoAddedByScript';
    const excludedItems = [];
    const eligibleItems = cart.items.filter(item => {
      // Check if item has properties and if it contains the script identifier
      let isScriptAdded = false;
      if (item.properties) {
        // Properties can be an object or array
        if (Array.isArray(item.properties)) {
          isScriptAdded = item.properties.some(prop => 
            (prop.name === scriptAddedPropertyKey || prop.key === scriptAddedPropertyKey) && 
            (prop.value === 'true' || prop.value === true)
          );
        } else if (typeof item.properties === 'object') {
          isScriptAdded = item.properties[scriptAddedPropertyKey] === 'true' || 
                         item.properties[scriptAddedPropertyKey] === true;
        }
      }
      
      if (isScriptAdded) {
        excludedItems.push({
          variantId: item.variant_id,
          productTitle: item.product_title || item.title,
          reason: 'script_added_property',
          quantity: item.quantity
        });
        return false;
      }
      
      return true; // Include all items that don't have the script-added property
    });
    
    // Log excluded items for debugging
    // if (excludedItems.length > 0) {
    //   console.log('Dollarlabs: Items excluded from groups:', excludedItems);
    // }
    
    // Always create the "All" group first (excluding script-added items)
    cartGroups["All"] = {
      items: eligibleItems,
      name: "All"
    };
    
    if (diagnostic) {
      diagnostic.groupCreation["All"] = {
        itemCount: eligibleItems.length,
        totalCartItems: cart.items.length,
        scriptAddedItemsExcluded: cart.items.length - eligibleItems.length,
        criteria: 'All items included by default (excluding script-added items)'
      };
    }
    
    // If no custom group criteria, return just the "All" group
    if (!groupCriteriaList || groupCriteriaList.length === 0) {
      return cartGroups;
    }
    
    // Create custom groups based on criteria
    for (const groupCriteria of groupCriteriaList) {
      if (groupCriteria && groupCriteria.rules && groupCriteria.rules.length > 0) {
        const groupName = groupCriteria.name || "CUSTOM_GROUP";
        
        if (diagnostic) {
          diagnostic.groupCreation[groupName] = {
            ruleCount: groupCriteria.rules.length,
            logic: groupCriteria.logic || 'AND',
            rules: groupCriteria.rules,
            itemEvaluations: []
          };
        }
        
        // Filter out ONLY script-added items before matching
        // Do NOT exclude by variant ID - this allows same product/variant to be both eligible and gift
        const scriptAddedPropertyKey = '_AutoAddedByScript';
        const groupExcludedItems = [];
        const eligibleItems = cart.items.filter(item => {
          // Check if item has the script-added property
          let isScriptAdded = false;
          if (item.properties) {
            if (Array.isArray(item.properties)) {
              isScriptAdded = item.properties.some(prop => 
                (prop.name === scriptAddedPropertyKey || prop.key === scriptAddedPropertyKey) && 
                (prop.value === 'true' || prop.value === true)
              );
            } else if (typeof item.properties === 'object') {
              isScriptAdded = item.properties[scriptAddedPropertyKey] === 'true' || 
                             item.properties[scriptAddedPropertyKey] === true;
            }
          }
          
          if (isScriptAdded) {
            groupExcludedItems.push({
              variantId: item.variant_id,
              productTitle: item.product_title || item.title,
              reason: 'script_added_property',
              quantity: item.quantity
            });
            return false;
          }
          
          return true; // Include all items that don't have the script-added property
        });
        
        if (groupExcludedItems.length > 0 && diagnostic) {
          diagnostic.groupCreation[groupName].excludedItems = groupExcludedItems;
        }
        
        const matchingItems = this.getMatchingItems(eligibleItems, groupCriteria.rules, groupCriteria.logic, diagnostic, groupName);
        
        cartGroups[groupName] = {
          items: matchingItems,
          name: groupName
        };
        
        if (diagnostic) {
          diagnostic.groupCreation[groupName].itemCount = matchingItems.length;
        }
      }
    }
    
    return cartGroups;
  }

  getMatchingItems(items, rules, logic = 'AND', diagnostic = null, groupName = null) {
    if (!rules || rules.length === 0) {
      return items;
    }

    return items.filter(item => {
      const itemEval = {
        product: item.product_title || item.title || 'Unknown Product',
        variantId: item.variant_id,
        ruleResults: [],
        finalResult: false
      };
      
      const ruleResults = rules.map((rule, index) => {
        const actualValue = this.getItemRuleValue(item, rule);
        const result = this.compareValues(actualValue, rule.operator, rule.value);
        
        itemEval.ruleResults.push({
          ruleIndex: index + 1,
          property: rule.property,
          operator: rule.operator,
          expectedValue: rule.value,
          actualValue: actualValue,
          result: result
        });
        
        return result;
      });
      
      const finalResult = logic === 'AND' ? ruleResults.every(result => result) : ruleResults.some(result => result);
      itemEval.finalResult = finalResult;
      
      if (diagnostic && groupName && diagnostic.groupCreation[groupName]) {
        diagnostic.groupCreation[groupName].itemEvaluations.push(itemEval);
      }
      
      return finalResult;
    });
  }

  getItemRuleValue(item, rule) {
    const { property } = rule;
    
    // Handle customer properties first
    if (property.startsWith('customer.')) {
      const customerProperty = property.replace('customer.', '');
      return this.getCustomerProperty(customerProperty);
    }
    // Handle cart-level properties
    else if (property.startsWith('cart.')) {
      const cartProperty = property.replace('cart.', '');
      return this.getCartProperty(cartProperty);
    }
    // Handle special properties
    else if (property === 'collections') {
      return this.getProductCollections(item);
    }
    // Handle nested properties
    else {
      return this.getNestedValue(item, property);
    }
  }

  // Step 3: Evaluate rules based on derived variables from specific groups
  evaluateRules(groupVariables, rules, logic = 'AND', diagnostic = null) {
    if (!rules || rules.length === 0) {
      if (diagnostic) {
        diagnostic.ruleEvaluations = [];
        diagnostic.rulesLogic = logic;
      }
      return true; // No rules means always true
    }

    const ruleResults = rules.map((rule, index) => {
      const { property, operator, value, group = "All" } = rule;
      
      // Get the variables for the specified group (defaults to "All")
      const derivedVariables = groupVariables[group];
      
      const ruleEval = {
        ruleIndex: index + 1,
        property: property,
        operator: operator,
        expectedValue: value,
        group: group,
        groupExists: !!derivedVariables,
        actualValue: null,
        result: false
      };
      
      if (!derivedVariables) {
        ruleEval.error = 'Group not found';
        ruleEval.availableGroups = Object.keys(groupVariables);
        if (diagnostic) diagnostic.ruleEvaluations.push(ruleEval);
        return false;
      }
      
      const actualValue = derivedVariables[property];
      const result = this.compareValues(actualValue, operator, value);
      
      ruleEval.actualValue = actualValue;
      ruleEval.result = result;
      
      if (diagnostic) diagnostic.ruleEvaluations.push(ruleEval);
      
      return result;
    });

    const finalResult = logic === 'AND' ? ruleResults.every(result => result) : ruleResults.some(result => result);
    
    if (diagnostic) {
      diagnostic.rulesLogic = logic;
      diagnostic.individualResults = ruleResults;
    }
    
    return finalResult;
  }

  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  }

  getProductCollections(item) {
    // Debug logging to see what properties exist
    // console.log('Dollarlabs: Item properties for', item.product_title, ':', item.properties);
    
    // First try to get enhanced collection data with title and handle
    const collectionsDataProperty = item.properties?._ProductCollectionsData;
    if (collectionsDataProperty) {
      // console.log('Dollarlabs: Using enhanced collections data:', collectionsDataProperty);
      return collectionsDataProperty.split(',').map(collectionData => {
        const parts = collectionData.trim().split('|');
        if (parts.length === 2) {
          return {
            handle: parts[0],
            title: parts[1]
          };
        }
        // Fallback if format is unexpected
        return {
          handle: parts[0],
          title: parts[0]
        };
      });
    }
    
    // Fallback to simple collections property for backward compatibility
    const collectionsProperty = item.properties?._ProductCollections;
    if (collectionsProperty) {
      // console.log('Dollarlabs: Using legacy collections data:', collectionsProperty);
      return collectionsProperty.split(',').map(handle => ({
        handle: handle.trim(),
        title: handle.trim() // Use handle as title for backward compatibility
      }));
    }
    
    // console.log('Dollarlabs: No collections data found for item');
    return [];
  }

  getCustomerProperty(property) {
    // Get customer data from window object
    const customerData = window.dollarLabsCustomerData;
    if (!customerData) {
      return null;
    }

    // Handle nested customer properties
    return this.getNestedValue(customerData, property);
  }

  getCartProperty(property) {
    // Get cart data from the current cart object
    // This would be passed in the context where it's needed
    const cart = this.currentCart || {};
    return this.getNestedValue(cart, property);
  }

  calculateCustomerDerivedVariables(currencyInfo = null) {
    const customerData = window.dollarLabsCustomerData;
    if (!customerData) {
      return {};
    }

    // Use currency info if provided, otherwise use current currency info or fallback
    const currInfo = currencyInfo || this.currentCurrencyInfo || { 
      currency: 'USD', 
      useBasedValues: 'base', 
      conversionRate: 1.0, 
      isBaseCurrency: true 
    };

    // Calculate customer-specific derived variables with currency conversion
    const rawTotalLifetimeSpent = customerData.total_spent || 0;
    const totalLifetimeSpent = this.convertCustomerAmount(rawTotalLifetimeSpent, customerData, currInfo);
    
    const orderCount = customerData.orders_count || 0;
    const averageOrderValue = orderCount > 0 ? totalLifetimeSpent / orderCount : 0;
    const addressCount = customerData.addresses_count || 0;
    
    // Convert store credit balance if available
    const rawStoreCreditBalance = customerData.store_credit_account?.balance || 0;
    const storeCreditBalance = this.convertCustomerAmount(rawStoreCreditBalance, customerData, currInfo);
    
    const isVip = customerData.tags?.includes('VIP') || false;
    const hasAccount = customerData.has_account || false;
    const acceptsMarketing = customerData.accepts_marketing || false;
    const isB2B = customerData.b2b || false;
    const taxExempt = customerData.tax_exempt || false;
    
    // Calculate days since last order
    let daysSinceLastOrder = null;
    if (customerData.last_order?.created_at) {
      const lastOrderDate = new Date(customerData.last_order.created_at);
      const now = new Date();
      daysSinceLastOrder = Math.floor((now - lastOrderDate) / (1000 * 60 * 60 * 24));
    }

    // Get last order amount in correct currency
    let lastOrderAmount = 0;
    if (customerData.last_order?.total_price) {
      lastOrderAmount = this.convertCustomerAmount(customerData.last_order.total_price, customerData, currInfo);
    }

    // Get default address country
    const defaultCountry = customerData.default_address?.country || null;
    const defaultCountryCode = customerData.default_address?.country_code || null;
    const defaultProvince = customerData.default_address?.province || null;
    const defaultProvinceCode = customerData.default_address?.province_code || null;

    // Calculate order frequency (orders per month)
    let orderFrequency = 0;
    if (customerData.orders?.length > 1) {
      const orders = customerData.orders.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const firstOrder = new Date(orders[0].created_at);
      const lastOrder = new Date(orders[orders.length - 1].created_at);
      const monthsDiff = (lastOrder - firstOrder) / (1000 * 60 * 60 * 24 * 30.44);
      orderFrequency = monthsDiff > 0 ? orderCount / monthsDiff : 0;
    }

    return {
      totalLifetimeSpent,
      orderCount,
      averageOrderValue,
      lastOrderAmount,
      addressCount,
      storeCreditBalance,
      isVip,
      hasAccount,
      acceptsMarketing,
      isB2B,
      taxExempt,
      daysSinceLastOrder,
      defaultCountry,
      defaultCountryCode,
      defaultProvince,
      defaultProvinceCode,
      orderFrequency
    };
  }

  /**
   * Converts customer monetary amounts (like total_spent) to the target currency
   */
  convertCustomerAmount(amount, customerData, currencyInfo) {
    if (!amount || amount === 0) return 0;

    // Determine the currency of the customer data
    // This could be from last order currency or store base currency
    let customerDataCurrency = this.currencyUtils.shopBaseCurrency; // Default assumption

    // Try to get currency from customer's last order
    if (customerData.last_order?.currency) {
      customerDataCurrency = customerData.last_order.currency;
    }

    // Convert if needed
    if (customerDataCurrency === currencyInfo.currency) {
      return amount; // No conversion needed
    }

    return this.currencyUtils.convertAmount(amount, customerDataCurrency, currencyInfo.currency);
  }

  compareValues(actual, operator, expected) {
    switch (operator) {
      case 'equals':
        return actual === expected;
      case 'not_equals':
        return actual !== expected;
      case 'contains':
        if (Array.isArray(actual)) {
          // Handle array of collection objects
          if (actual.length > 0 && typeof actual[0] === 'object' && actual[0].handle) {
            return actual.some(item => 
              item.handle === expected || 
              item.title === expected ||
              item.handle.toLowerCase() === expected.toLowerCase() ||
              item.title.toLowerCase() === expected.toLowerCase()
            );
          }
          // Handle regular array
          return actual.includes(expected);
        }
        return String(actual).includes(expected);
      case 'not_contains':
        if (Array.isArray(actual)) {
          // Handle array of collection objects
          if (actual.length > 0 && typeof actual[0] === 'object' && actual[0].handle) {
            return !actual.some(item => 
              item.handle === expected || 
              item.title === expected ||
              item.handle.toLowerCase() === expected.toLowerCase() ||
              item.title.toLowerCase() === expected.toLowerCase()
            );
          }
          // Handle regular array
          return !actual.includes(expected);
        }
        return !String(actual).includes(expected);
      case 'greater_than':
        return Number(actual) > Number(expected);
      case 'less_than':
        return Number(actual) < Number(expected);
      case 'greater_than_or_equal':
        return Number(actual) >= Number(expected);
      case 'less_than_or_equal':
        return Number(actual) <= Number(expected);
      case 'in':
        return Array.isArray(expected) ? expected.includes(actual) : false;
      case 'not_in':
        return Array.isArray(expected) ? !expected.includes(actual) : false;
      case 'exists':
        return actual !== null && actual !== undefined && actual !== '';
      case 'not_exists':
        return actual === null || actual === undefined || actual === '';
      default:
        return false;
    }
  }

  calculateDerivedVariables(items, currencyInfo = null) {
    // Use currency info if provided, otherwise use current currency info or fallback
    const currInfo = currencyInfo || this.currentCurrencyInfo || { 
      currency: 'USD', 
      useBasedValues: 'base', 
      conversionRate: 1.0, 
      isBaseCurrency: true 
    };

    // Calculate amounts using appropriate currency values
    const totalCartAmount = items.reduce((sum, item) => {
      return sum + this.currencyUtils.getItemPrice(item, currInfo, 'line_price');
    }, 0);

    const subtotalCartAmount = items.reduce((sum, item) => {
      return sum + this.currencyUtils.getItemPrice(item, currInfo, 'original_line_price');
    }, 0);

    const finalCartAmount = items.reduce((sum, item) => {
      return sum + this.currencyUtils.getItemPrice(item, currInfo, 'final_line_price');
    }, 0);

    const discountAmount = subtotalCartAmount - finalCartAmount;

    const lineCount = items.length;
    const quantitySum = items.reduce((sum, item) => sum + item.quantity, 0);
    const uniqueProductCount = new Set(items.map(item => item.product_id)).size;
    const uniqueVariantCount = new Set(items.map(item => item.variant_id)).size;

    // Calculate average values
    const averageItemPrice = lineCount > 0 ? totalCartAmount / lineCount : 0;
    const averageQuantityPerLine = lineCount > 0 ? quantitySum / lineCount : 0;

    // Get customer derived variables
    const customerDerived = this.calculateCustomerDerivedVariables(currInfo);

    return {
      // Cart amount variables (currency-aware)
      totalCartAmount,
      subtotalCartAmount,
      finalCartAmount,
      discountAmount,
      
      // Count variables
      lineCount,
      quantitySum,
      uniqueProductCount,
      uniqueVariantCount,
      cartGroupExists: items.length > 0,
      
      // Average variables
      averageItemPrice,
      averageQuantityPerLine,
      
      // Currency metadata
      calculationCurrency: currInfo.currency,
      usingBaseCurrency: currInfo.isBaseCurrency,
      currencyConversionRate: currInfo.conversionRate,
      
      // Customer-specific derived variables
      ...customerDerived
    };
  }

  analyzeCart(cart) {
    // This is a wrapper around calculateDerivedVariables for the entire cart
    return this.calculateDerivedVariables(cart.items || []);
  }

  analyzeCartGroup(cart, groupName) {
    // Find the group in the current configuration
    const currentConfig = this.config.find(config => {
      return config.groupCriteria && config.groupCriteria.some(group => group.name === groupName);
    });
    
    if (!currentConfig) {
      // console.warn('Dollarlabs: Group not found in current configuration:', groupName);
      return null;
    }
    
    // Create cart groups to get the specific group's items
    const cartGroups = this.createCartGroups(cart, currentConfig.groupCriteria);
    const groupItems = cartGroups[groupName]?.items || [];
    
    // Use the existing calculateDerivedVariables method for the group items
    return this.calculateDerivedVariables(groupItems);
  }

  async executeActions(actions, cart, diagnostic = null) {
    const executedActions = [];
    
    for (const action of actions) {
      const actionResult = await this.executeAction(action, cart, diagnostic);
      if (diagnostic) {
        executedActions.push({
          type: action.type,
          variantId: action.variantId || action.productId,
          quantity: action.quantity,
          result: actionResult
        });
      }
    }
    
    return executedActions;
  }

  async checkAutoRemoveProducts(actions, cart, diagnostic = null) {
    const executedActions = [];
    
    // Only check actions that have auto_remove: true
    const autoRemoveActions = actions.filter(action => action.auto_remove === true && action.type === 'add');
    
    for (const action of autoRemoveActions) {
      const targetVariantId = action.variantId || action.productId;
      
      // Handle dynamic quantities for auto-remove
      let targetQuantity = action.quantity;
      if (typeof action.quantity === 'string' && action.quantity.startsWith('quantitySum')) {
        // Parse quantity reference (e.g., "quantitySum", "quantitySum:GROUP_NAME", or "quantitySum:GROUP_NAME/3")
        const quantityParts = action.quantity.split(':');
        const isGroupSpecific = quantityParts.length > 1;
        const groupNamePart = isGroupSpecific ? quantityParts[1] : null;
        
        // Check if there's a division factor (e.g., "GROUP_NAME/3" or "/3")
        let groupName = groupNamePart;
        let divisor = 1;
        if (groupNamePart && groupNamePart.includes('/')) {
          const groupParts = groupNamePart.split('/');
          groupName = groupParts[0] || null; // Empty string becomes null
          divisor = parseFloat(groupParts[1]) || 1;
        } else if (!isGroupSpecific && action.quantity.includes('/')) {
          // Handle case like "quantitySum/3" (no group name)
          const parts = action.quantity.split('/');
          divisor = parseFloat(parts[1]) || 1;
          groupName = null;
        }
        
        const cartAnalysis = this.analyzeCart(cart);
        
        if (groupName) {
          // Use specific group's quantity sum
          const groupAnalysis = this.analyzeCartGroup(cart, groupName);
          const rawQuantity = groupAnalysis ? groupAnalysis.quantitySum : 0;
          targetQuantity = Math.floor(rawQuantity / divisor);
        } else {
          // Use entire cart's quantity sum
          const rawQuantity = cartAnalysis.quantitySum;
          targetQuantity = Math.floor(rawQuantity / divisor);
        }
      }
      
      // Check existing quantity of ONLY script-added items (for auto-remove)
      // We only want to remove items that were added by the script
      const scriptAddedPropertyKey = '_AutoAddedByScript';
      const scriptAddedItems = cart.items.filter(item => {
        if (item.variant_id !== targetVariantId) return false;
        
        // Only count items with the script-added property
        if (item.properties) {
          if (Array.isArray(item.properties)) {
            return item.properties.some(prop => 
              (prop.name === scriptAddedPropertyKey || prop.key === scriptAddedPropertyKey) && 
              (prop.value === 'true' || prop.value === true)
            );
          } else if (typeof item.properties === 'object') {
            return item.properties[scriptAddedPropertyKey] === 'true' || 
                   item.properties[scriptAddedPropertyKey] === true;
          }
        }
        return false;
      });
      
      const scriptAddedQuantity = scriptAddedItems.reduce((sum, item) => sum + item.quantity, 0);
      
      // If script-added items exist in cart, remove them since rules failed
      if (scriptAddedQuantity > 0) {
        try {
          // Remove script-added items using their line item keys
          await this.removeScriptAddedItems(scriptAddedItems, cart);
          const actionResult = {
            status: 'executed',
            action: 'auto_removed',
            reason: 'Rules no longer pass',
            quantityRemoved: scriptAddedQuantity,
            autoRemove: true
          };
          
          if (diagnostic) {
            executedActions.push({
              type: action.type,
              variantId: targetVariantId,
              quantity: action.quantity,
              result: actionResult
            });
          }
          
          // console.log('Dollarlabs: Auto-removed script-added product due to failed rules:', {
          //   variantId: targetVariantId,
          //   quantityRemoved: scriptAddedQuantity,
          //   configName: diagnostic?.configName,
          //   note: 'Only script-added items removed, manually added items preserved'
          // });
        } catch (error) {
          // console.error('Dollarlabs: Error during auto-remove:', error);
          if (diagnostic) {
            executedActions.push({
              type: action.type,
              variantId: targetVariantId,
              quantity: action.quantity,
              result: {
                status: 'error',
                error: error.message
              }
            });
          }
        }
      }
    }
    
    return executedActions;
  }

  async executeAction(action, cart, diagnostic = null) {
    const { type, productId, variantId, quantity, auto_remove = false } = action;
    
    try {
      if (type === 'add') {
        // Fetch fresh cart to ensure we have the latest state
        // This prevents issues where the cart parameter might be stale
        const freshCart = await this.fetchCart();
        
        // Check if item already exists in required quantity before adding
        const targetVariantId = variantId || productId;
        
        // For action variants (gifts), count ONLY script-added instances
        // This allows manually added items to count toward eligibility, but only script-added items count as gifts
        // For regular items, exclude script-added items to avoid double-counting
        let existingQuantity;
        if (this.actionVariantIds.has(targetVariantId)) {
          // Count ONLY script-added instances for action variants (gifts) - use fresh cart
          const scriptAddedPropertyKey = '_AutoAddedByScript';
          existingQuantity = freshCart.items
            .filter(item => {
              if (item.variant_id !== targetVariantId) return false;
              
              // Only count items with the script-added property
              if (item.properties) {
                if (Array.isArray(item.properties)) {
                  return item.properties.some(prop => 
                    (prop.name === scriptAddedPropertyKey || prop.key === scriptAddedPropertyKey) && 
                    (prop.value === 'true' || prop.value === true)
                  );
                } else if (typeof item.properties === 'object') {
                  return item.properties[scriptAddedPropertyKey] === 'true' || 
                         item.properties[scriptAddedPropertyKey] === true;
                }
              }
              return false;
            })
            .reduce((sum, item) => sum + item.quantity, 0);
          // console.log('Dollarlabs: Action variant quantity check (script-added only):', {
          //   targetVariantId,
          //   existingQuantity,
          //   totalItems: freshCart.items.filter(item => item.variant_id === targetVariantId).length,
          //   scriptAddedOnly: true
          // });
        } else {
          // Exclude script-added items for regular items - use fresh cart
          existingQuantity = this.getVariantQuantityInCart(freshCart, targetVariantId, true);
        }
        
        // Handle dynamic quantities
        let targetQuantity = quantity;
        if (typeof quantity === 'string' && quantity.startsWith('quantitySum')) {
          // Parse quantity reference (e.g., "quantitySum", "quantitySum:GROUP_NAME", or "quantitySum:GROUP_NAME/3")
          const quantityParts = quantity.split(':');
          const isGroupSpecific = quantityParts.length > 1;
          const groupNamePart = isGroupSpecific ? quantityParts[1] : null;
          
          // Check if there's a division factor (e.g., "GROUP_NAME/3" or "/3")
          let groupName = groupNamePart;
          let divisor = 1;
          if (groupNamePart && groupNamePart.includes('/')) {
            const groupParts = groupNamePart.split('/');
            groupName = groupParts[0] || null; // Empty string becomes null
            divisor = parseFloat(groupParts[1]) || 1;
          } else if (!isGroupSpecific && quantity.includes('/')) {
            // Handle case like "quantitySum/3" (no group name)
            const parts = quantity.split('/');
            divisor = parseFloat(parts[1]) || 1;
            groupName = null;
          }
          
          // Calculate dynamic quantity based on fresh cart analysis
          const cartAnalysis = this.analyzeCart(freshCart);
          
          if (groupName) {
            // Use specific group's quantity sum from fresh cart
            const groupAnalysis = this.analyzeCartGroup(freshCart, groupName);
            const rawQuantity = groupAnalysis ? groupAnalysis.quantitySum : 0;
            targetQuantity = Math.floor(rawQuantity / divisor);
            // console.log('Dollarlabs: Group-specific dynamic quantity calculated:', {
            //   originalQuantity: quantity,
            //   groupName: groupName,
            //   rawQuantity: rawQuantity,
            //   divisor: divisor,
            //   calculatedQuantity: targetQuantity,
            //   groupQuantitySum: groupAnalysis ? groupAnalysis.quantitySum : 0
            // });
          } else {
            // Use entire cart's quantity sum
            const rawQuantity = cartAnalysis.quantitySum;
            targetQuantity = Math.floor(rawQuantity / divisor);
            // console.log('Dollarlabs: Global dynamic quantity calculated:', {
            //   originalQuantity: quantity,
            //   rawQuantity: rawQuantity,
            //   divisor: divisor,
            //   calculatedQuantity: targetQuantity,
            //   cartQuantitySum: cartAnalysis.quantitySum
            // });
          }
        }
        
        // Quantity correction logic: update to exact quantity specified
        if (existingQuantity !== targetQuantity) {
          if (existingQuantity > targetQuantity) {
            // Reduce quantity
            const quantityToRemove = existingQuantity - targetQuantity;
            await this.updateCartQuantity(targetVariantId, targetQuantity, freshCart);
            return {
              status: 'executed',
              action: 'quantity_corrected',
              quantityRemoved: quantityToRemove,
              existingQuantity: existingQuantity,
              targetQuantity: targetQuantity,
              finalQuantity: targetQuantity
            };
          } else {
            // Add more quantity
            const quantityToAdd = targetQuantity - existingQuantity;
            // Pass any additional properties from the action config
            const actionProperties = action.properties || {};
            await this.addToCart(targetVariantId, quantityToAdd, actionProperties);
            return {
              status: 'executed',
              action: existingQuantity > 0 ? 'quantity_corrected' : 'added',
              quantityAdded: quantityToAdd,
              existingQuantity: existingQuantity,
              targetQuantity: targetQuantity,
              finalQuantity: targetQuantity
            };
          }
        }
        
        // If quantity is already correct, skip
        return {
          status: 'skipped',
          reason: 'Target quantity already met',
          existingQuantity: existingQuantity,
          targetQuantity: targetQuantity
        };
        
      } else if (type === 'remove') {
        await this.removeFromCart(variantId || productId, quantity, cart);
        return {
          status: 'executed',
          action: 'removed',
          quantityRemoved: quantity
        };
      }
    } catch (error) {
      // console.error('Dollarlabs: Error executing action ' + type + ':', error);
      return {
        status: 'error',
        error: error.message
      };
    }
  }

  async addToCart(variantId, quantity, properties = {}) {
    // console.log('Dollarlabs: Adding to cart - Variant ID:', variantId, 'Quantity:', quantity);
    
    // Set flag to indicate this cart change is from automation
    this.isAutomationTriggered = true;
    this.lastActionTimestamp = Date.now();
    
    // Add script identifier property to all items added by automation
    const itemProperties = {
      '_AutoAddedByScript': 'true',
      ...properties
    };
    
    // Build the request payload
    const payload = {
      items: [{
        id: variantId,
        quantity: quantity
      }]
    };
    
    // Add properties if we have any
    // Shopify Cart API accepts properties as an object (not array)
    if (Object.keys(itemProperties).length > 0) {
      payload.items[0].properties = itemProperties;
    }
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Try to get the error message from the response
        let errorMessage = response.statusText;
        try {
          const errorData = await response.json();
          if (errorData.description || errorData.message) {
            errorMessage = errorData.description || errorData.message;
          }
        } catch (e) {
          // If response is not JSON, use statusText
          const responseText = await response.text();
          if (responseText) {
            errorMessage = responseText;
          }
        }
        
        // console.error('Dollarlabs: Failed to add item to cart:', {
        //   status: response.status,
        //   statusText: response.statusText,
        //   errorMessage: errorMessage,
        //   payload: payload
        // });
        this.isAutomationTriggered = false; // Reset flag on error
        throw new Error(`Failed to add item to cart: ${errorMessage} (Status: ${response.status})`);
      }

      const result = await response.json();
      // console.log('Dollarlabs: Successfully added to cart:', result);
      
      // Refresh cart UI after successful addition
      this.refreshCartUI();
      
      return result;
    } catch (error) {
      // console.error('Dollarlabs: Error in addToCart:', error);
      this.isAutomationTriggered = false; // Reset flag on error
      throw error;
    }
  }

  /**
   * Removes script-added items from cart using their line item keys
   * This ensures we only remove items added by the script, not manually added ones
   * @param {Array} scriptAddedItems - Array of cart items with _AutoAddedByScript property
   * @param {Object} cart - The cart object (for reference)
   */
  async removeScriptAddedItems(scriptAddedItems, cart) {
    // console.log('Dollarlabs: Removing script-added items from cart:', scriptAddedItems.map(item => ({
    //   key: item.key,
    //   variantId: item.variant_id,
    //   quantity: item.quantity
    // })));
    
    // Set flag to indicate this cart change is from automation
    this.isAutomationTriggered = true;
    this.lastActionTimestamp = Date.now();
    
    // Remove each script-added item using its line item key
    // According to Shopify Cart API: https://shopify.dev/docs/api/ajax/reference/cart#post--locale-cart-changejs
    // We use the line item 'key' property, not variant ID
    for (const item of scriptAddedItems) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: item.key,  // Use line item key, not variant ID
          quantity: 0    // Set quantity to 0 to remove
        })
      });
      
      if (!response.ok) {
        // console.error('Dollarlabs: Failed to remove script-added item from cart:', {
        //   key: item.key,
        //   variantId: item.variant_id,
        //   error: response.statusText
        // });
      } else {
        // console.log('Dollarlabs: Successfully removed script-added item:', {
        //   key: item.key,
        //   variantId: item.variant_id,
        //   quantity: item.quantity
        // });
      }
    }
    
    // Refresh cart UI after removal
    this.refreshCartUI();
  }

  async removeFromCart(variantId, quantity, cart) {
    // console.log('Dollarlabs: Removing from cart - Variant ID:', variantId, 'Quantity:', quantity);
    
    // Set flag to indicate this cart change is from automation
    this.isAutomationTriggered = true;
    this.lastActionTimestamp = Date.now();
    
    // Find items to remove from cart
    // Note: This method is used for general removal, but for script-added items,
    // use removeScriptAddedItems() instead to preserve manually added items
    const itemsToRemove = cart.items
      .filter(item => item.variant_id === variantId)
      .slice(0, quantity);
    
    // console.log('Dollarlabs: Items to remove:', itemsToRemove.map(item => ({
    //   key: item.key,
    //   variantId: item.variant_id,
    //   quantity: item.quantity
    // })));
    
    // Remove items using their line item keys (not variant IDs)
    // According to Shopify Cart API: https://shopify.dev/docs/api/ajax/reference/cart#post--locale-cart-changejs
    for (const item of itemsToRemove) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: item.key,  // Use line item key, not variant ID
          quantity: 0    // Set quantity to 0 to remove
        })
      });
      
      if (!response.ok) {
        // console.error('Dollarlabs: Failed to remove item from cart:', response.statusText);
      } else {
        // console.log('Dollarlabs: Successfully removed item:', item.key);
      }
    }
    
    // Refresh cart UI after removal
    this.refreshCartUI();
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  // Helper method to get current quantity of a variant in the cart
  // Optionally exclude script-added items
  getVariantQuantityInCart(cart, variantId, excludeScriptAdded = false) {
    let items = cart.items.filter(item => item.variant_id === variantId);
    
    // Filter out script-added items if requested
    if (excludeScriptAdded) {
      const scriptAddedPropertyKey = '_AutoAddedByScript';
      items = items.filter(item => {
        if (item.properties) {
          if (Array.isArray(item.properties)) {
            return !item.properties.some(prop => 
              (prop.name === scriptAddedPropertyKey || prop.key === scriptAddedPropertyKey) && 
              (prop.value === 'true' || prop.value === true)
            );
          } else if (typeof item.properties === 'object') {
            return item.properties[scriptAddedPropertyKey] !== 'true' && 
                   item.properties[scriptAddedPropertyKey] !== true;
          }
        }
        return true;
      });
    }
    
    return items.reduce((total, item) => total + item.quantity, 0);
  }

  // Helper method to check if product should be removed for auto_remove logic
  async shouldRemoveProductForAutoRemove(action, cart) {
    // Create a temporary cart without the target product to test conditions
    const targetVariantId = action.variantId || action.productId;
    const tempCart = {
      ...cart,
      items: cart.items.filter(item => item.variant_id !== targetVariantId)
    };
    
    // Process the same config again but with the temporary cart
    // If rules still pass without this product, it means the product should be removed
    try {
      // We'll simulate the rule evaluation without the product
      // For simplicity, we assume if cart total drops below a threshold, remove the product
      // This is a basic implementation - can be enhanced based on specific requirements
      
      const currencyInfo = this.currentCurrencyInfo;
      const tempCartGroups = this.createCartGroups(tempCart, this.config[0]?.groupCriteria || []);
      const tempGroupVariables = {};
      
      for (const [groupName, group] of Object.entries(tempCartGroups)) {
        tempGroupVariables[groupName] = this.calculateDerivedVariables(group.items, currencyInfo);
      }
      
      // Check if the current config rules would still pass
      const currentConfig = this.getCurrentConfigForAction(action);
      if (currentConfig && currentConfig.rules) {
        const rulesResult = this.evaluateRules(tempGroupVariables, currentConfig.rules, currentConfig.rulesLogic);
        // If rules don't pass anymore, the product should be removed
        return !rulesResult;
      }
      
      return false; // Default to not removing if we can't determine
    } catch (error) {
      // console.error('Dollarlabs: Error in shouldRemoveProductForAutoRemove:', error);
      return false;
    }
  }

  // Helper method to get the current config that triggered this action
  getCurrentConfigForAction(action) {
    // This is a simplified approach - in a real implementation, you'd track which config triggered the action
    // For now, we'll use the first config that has matching actions
    return this.config.find(configItem => 
      configItem.actions && configItem.actions.some(configAction => 
        (configAction.variantId || configAction.productId) === (action.variantId || action.productId)
      )
    );
  }

  // Helper method to update cart item quantity to exact amount
  async updateCartQuantity(variantId, newQuantity, cart) {
    // console.log('Dollarlabs: Updating cart quantity - Variant ID:', variantId, 'New Quantity:', newQuantity);
    
    // Set flag to indicate this cart change is from automation
    this.isAutomationTriggered = true;
    this.lastActionTimestamp = Date.now();
    
    // Find the cart item(s) with this variant ID
    const cartItems = cart.items.filter(item => item.variant_id === variantId);
    
    if (cartItems.length === 0) {
      // console.log('Dollarlabs: No items found with variant ID:', variantId);
      return;
    }
    
    try {
      if (newQuantity === 0) {
        // Remove all items of this variant
        for (const item of cartItems) {
          await this.removeCartItem(item.key);
        }
      } else {
        // Update the first item to the new quantity and remove others
        const primaryItem = cartItems[0];
        
        // Update primary item quantity
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: primaryItem.key,
            quantity: newQuantity
          })
        });
        
        if (!response.ok) {
          // console.error('Dollarlabs: Failed to update cart item quantity:', response.statusText);
          throw new Error(`Failed to update cart item quantity: ${response.statusText}`);
        }
        
        // Remove any additional items of the same variant
        for (let i = 1; i < cartItems.length; i++) {
          await this.removeCartItem(cartItems[i].key);
        }
        
        // console.log('Dollarlabs: Successfully updated cart item quantity to:', newQuantity);
      }
      
      // Refresh cart UI after quantity update
      this.refreshCartUI();
    } catch (error) {
      // console.error('Dollarlabs: Error updating cart quantity:', error);
      this.isAutomationTriggered = false; // Reset flag on error
      throw error;
    }
  }

  // Helper method to remove a specific cart item by key
  async removeCartItem(itemKey) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0
      })
    });
    
    if (!response.ok) {
      // console.error('Dollarlabs: Failed to remove cart item:', response.statusText);
      throw new Error(`Failed to remove cart item: ${response.statusText}`);
    }
    
    // console.log('Dollarlabs: Successfully removed cart item:', itemKey);
  }

  /**
   * Refreshes the cart UI to reflect changes made by automation
   * Triggers theme-specific cart refresh mechanisms
   */
  refreshCartUI() {
    // Small delay to ensure cart API has processed the change
    setTimeout(async () => {
      try {
        // Check if we're on the cart page
        const isCartPage = window.location.pathname === '/cart' || 
                          window.location.pathname === '/fr/cart' ||
                          window.location.pathname.endsWith('/cart');
        
        // Fetch updated cart
        const cart = await this.fetchCart();
        
        // If on cart page, reload the page to show updated cart
        if (isCartPage) {
          // console.log('Dollarlabs: Reloading cart page to show updates');
          window.location.reload();
          return; // Exit early since we're reloading
        }
        
        // Update cart count in header (common.js pattern)
        // Check for jQuery availability
        if (typeof window.jQuery !== 'undefined' || typeof window.$ !== 'undefined') {
          const $ = window.jQuery || window.$;
          
          // Update cart count
          const $cartTotal = $('#cart-total');
          if ($cartTotal.length) {
            $cartTotal.html(cart.item_count);
            // console.log('Dollarlabs: Cart count updated to', cart.item_count);
          }
          
          // Refresh cart dropdown (common.js pattern)
          const $cartUl = $('#cart > ul');
          if ($cartUl.length) {
            const localePrefix = window.location.pathname.startsWith('/fr') ? '/fr' : '';
            $cartUl.load(localePrefix + ' #cart > ul li', function() {
              // console.log('Dollarlabs: Cart dropdown refreshed');
            });
          }
        } else {
          // Fallback: Update cart count using vanilla JS if jQuery is not available
          const cartTotalEl = document.getElementById('cart-total') || 
                             document.querySelector('#cart-total') ||
                             document.querySelector('[data-cart-count]');
          if (cartTotalEl) {
            cartTotalEl.textContent = cart.item_count;
            cartTotalEl.innerHTML = cart.item_count;
            // console.log('Dollarlabs: Cart count updated (vanilla JS) to', cart.item_count);
          }
        }
        
        // Trigger BOLD apps cart update event (if BOLD is available)
        if (window.BOLD && BOLD.common && BOLD.common.eventEmitter && 
            typeof BOLD.common.eventEmitter.emit === 'function') {
          BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded');
          // console.log('Dollarlabs: BOLD cart event emitted');
        }
        
        // Trigger Shopify.onCartUpdate (for ajaxify.js and other cart systems)
        if (typeof Shopify !== 'undefined' && typeof Shopify.onCartUpdate === 'function') {
          Shopify.onCartUpdate(cart);
          // console.log('Dollarlabs: Shopify.onCartUpdate called');
        }
        
        // Dispatch custom event for other scripts that might be listening
        const cartUpdateEvent = new CustomEvent('dollarlabs:cart_updated', { 
          detail: { cart: cart } 
        });
        window.dispatchEvent(cartUpdateEvent);
        
        // console.log('Dollarlabs: Cart UI refresh completed');
      } catch (error) {
        // console.error('Dollarlabs: Error refreshing cart UI:', error);
      }
    }, 200); // 200ms delay to ensure cart API has processed
  }
}

// Cart Watcher Class (from the provided documentation)
class CartWatcher {
  init() {
    // console.log('Dollarlabs: Initializing CartWatcher');
    this.emitCartChanges().then(() => {
      this.observeCartChanges();
      // console.log('Dollarlabs: CartWatcher initialization complete');
    });
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  storeCart(cart) {
    localStorage.setItem('cart', JSON.stringify(cart));
  }

  storedCart() {
    return JSON.parse(localStorage.getItem('cart')) || { items: [] };
  }

  findCartChanges(oldCart, newCart) {
    const onlyInLeft = (l, r) => l.filter(li => !r.some(ri => li.key == ri.key));
    let result = {
      added: onlyInLeft(newCart.items, oldCart.items),
      removed: onlyInLeft(oldCart.items, newCart.items),
    };

    oldCart.items.forEach(oi => {
      const ni = newCart.items.find(i => i.key == oi.key && i.quantity != oi.quantity);
      if (!ni) return;
      let quantity = ni.quantity - oi.quantity;
      let item = { ...ni };
      item.quantity = Math.abs(quantity);
      quantity > 0
        ? result.added.push(item)
        : result.removed.push(item)
    });

    return result;
  }

  async emitCartChanges() {
    const newCart = await this.fetchCart();
    const oldCart = this.storedCart();
    const changes = this.findCartChanges(oldCart, newCart);

    if (changes.added.length > 0 || changes.removed.length > 0) {
      // console.log('Dollarlabs: Cart changes detected:', changes);
      
      // Add small delay to ensure automation flag is properly handled
      setTimeout(() => {
        const event = new CustomEvent("cart_changed", { detail: changes });
        window.dispatchEvent(event);
      }, 100);
    } else {
      // No changes, dispatch immediately
      const event = new CustomEvent("cart_changed", { detail: changes });
      window.dispatchEvent(event);
    }

    this.storeCart(newCart);
  }

  observeCartChanges() {
    const cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
        const isCartChangeRequest = /\/cart\//.test(entry.name);
        if (isValidRequestType && isCartChangeRequest) {
          this.emitCartChanges();
        }
      });
    });
    cartObserver.observe({ entryTypes: ["resource"] });
  }
}

// Example configuration using the new multi-group structure
const exampleConfig = [
  // {
  //   // Config 1: VIP Snowboard Customers with Multiple Groups
  //   name: "VIP_SNOWBOARD_PROMOTION",
    
  //   // Step 1: Create multiple cart groups
  //   groupCriteria: [
  //     {
  //       name: "VIP_SNOWBOARDS",
  //       logic: "AND", // How to combine multiple rules
  //       rules: [
  //         {
  //           property: "product_type",
  //           operator: "equals",
  //           value: "snowboard"
  //         },
  //         {
  //           property: "collections",
  //           operator: "contains",
  //           value: "hydrogen"
  //         },
  //         {
  //           property: "customer.tags",
  //           operator: "contains",
  //           value: "VIP"
  //         }
  //       ]
  //     }
  //   ],
    
  //   // Step 3: Rules based on derived variables from specific groups
  //   rulesLogic: "AND", // How to combine multiple rules
  //   rules: [
  //     {
  //       property: "totalCartAmount",
  //       operator: "greater_than",
  //       value: 3000, // $3000 in cents
  //       group: "VIP_SNOWBOARDS" // Check VIP_SNOWBOARDS group variables
  //     },
  //     {
  //       property: "lineCount",
  //       operator: "greater_than",
  //       value: 2,
  //       group: "All" // Check All group variables
  //     }
  //   ],
    
  //   // Actions to execute if all rules are true
  //   actions: [
  //     {
  //       type: "add",
  //       variantId: 40649846456384, // Replace with actual variant ID
  //       quantity: 1
  //     }
  //   ]
  // },
  {
    // Config 1: Gift for every 3 units from specific product group
    name: "GIFT_FOR_EVERY_3_UNITS",
    
    // Currency configuration
    currencyMode: "auto", // Use auto mode for currency handling
    
    // Step 1: Create a group for the specific products
    groupCriteria: [
      {
        name: "GIFT_ELIGIBLE_PRODUCTS",
        logic: "OR", // Product ID matches any of the specified IDs
        rules: [
          {
            property: "product_id",
            operator: "in",
            value: [6570676584519, 6570676682823, 6614323691591, 6592841777223, 6729461006407, 6944235290695]
          }
        ]
      }
    ],
    
    // Step 3: Rules - check if we have at least 3 units in the group
    rulesLogic: "AND",
    rules: [
      {
        property: "quantitySum",
        operator: "greater_than_or_equal",
        value: 3,
        group: "GIFT_ELIGIBLE_PRODUCTS"
      }
    ],
    
    // Actions to execute - add 1 gift for every 3 units
    actions: [
      {
        type: "add",
        variantId: 40638781358151, // Gift variant ID
        quantity: "quantitySum:GIFT_ELIGIBLE_PRODUCTS/3", // Dynamic quantity: floor(quantitySum / 3)
        auto_remove: true // Will remove when conditions are not met
      }
    ]
  },
//   {
//     // Config 2: Currency-aware bulk purchase incentive
//     name: "BULK_PURCHASE_INCENTIVE",
    
//     // Currency configuration - use base currency for all calculations
//     currencyMode: "base", // Options: "base", "presentment", "specific"
//     // specificCurrency: "USD", // Only used when currencyMode is "specific"
    
//     // Step 1: No custom groups (will only use "All" group)
//     groupCriteria: [],
    
//     // Step 3: Rules based on derived variables from "All" group
//     rulesLogic: "AND",
//     rules: [
//       {
//         property: "quantitySum",
//         operator: "greater_than",
//         value: 10,
//         group: "All" // Explicitly specify "All" group (optional, it's the default)
//       },
//       {
//         property: "totalCartAmount", 
//         operator: "greater_than",
//         value: 500000 // $5000 in base currency (cents)
//         // group defaults to "All" if not specified
//       }
//     ],
    
//     // Actions to execute
//     actions: [
//       {
//         type: "add",
//         variantId: 40649846456384, // Replace with bulk bonus variant ID
//         quantity: 1,
//         auto_remove: true // Will remove when conditions are not met
//       }
//     ]
//   },
  
  // Config 3: Multi-currency example using presentment currency
  // {
  //   name: "PRESENTMENT_CURRENCY_PROMOTION",
    
  //   // Use presentment currency (what customer sees)
  //   currencyMode: "presentment",
    
  //   // Step 1: No custom groups
  //   groupCriteria: [],
    
  //   // Step 3: Rules based on presentment currency values
  //   rulesLogic: "AND",
  //   rules: [
  //     {
  //       property: "totalCartAmount",
  //       operator: "greater_than",
  //       value: 10000 // $100 in presentment currency (cents)
  //     },
  //     {
  //       property: "lineCount",
  //       operator: "greater_than",
  //       value: 2
  //     }
  //   ],
    
  //   actions: [
  //     {
  //       type: "add",
  //       variantId: 40649846456384,
  //       quantity: 1
  //     }
  //   ]
  // },
  
  // Config 4: Specific currency example (USD only)
  // {
  //   name: "USD_ONLY_PROMOTION",
    
  //   // Only activate for USD transactions
  //   currencyMode: "specific",
  //   specificCurrency: "USD",
    
  //   groupCriteria: [],
    
  //   rulesLogic: "AND",
  //   rules: [
  //     {
  //       property: "totalCartAmount",
  //       operator: "greater_than",
  //       value: 20000 // $200 USD
  //     },
  //     {
  //       property: "calculationCurrency",
  //       operator: "equals",
  //       value: "USD"
  //     }
  //   ],
    
  //   actions: [
  //     {
  //       type: "add",
  //       variantId: 40649846456384,
  //       quantity: 1
  //     }
  //   ]
  // }
  
];

// Initialize the cart automation when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // console.log('Dollarlabs: DOM loaded, initializing CartAutomation with config');
  // Replace with your actual configuration
  const cartAutomation = new CartAutomation(exampleConfig);
  
  // Make it globally available for debugging
  window.cartAutomation = cartAutomation;
  // console.log('Dollarlabs: CartAutomation available globally as window.cartAutomation');


});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CartAutomation, CartWatcher };
}
